/**
 * Build step 10 — Money view and Zoho sync (ADR-001 §11.12, §8.2, §8.10, §10.5; operator decisions 2, 16).
 *
 * EVERY route here is managers only: it needs `money.read` (founder, ops_ad) and anyone else gets
 * `403 forbidden` — including the `{id}` routes (deny: forbidden), because the Money hub is absent from a
 * member's menu and its existence is not a secret; only its numbers are. Writes are audited (real actor +
 * acting-as) and refused while viewing as someone. Zoho is read-only from our side: nothing here writes to Zoho
 * or overrides a Zoho figure.
 */
import { IdempotencyHeaders } from './common.js';
import { Escalation } from './requests.js';
import { Task } from './tasks.js';
import { AckFlagBody, EscalateFlagBody, FlagHit, FlagHitIdParams, FlagHitPage, FlagTaskBody, IntegrationList, InvoiceLineIdParams, JobRunPage, ListFlagHitsQuery, ListInvoiceLinesQuery, ListJobRunsQuery, MapInvoiceLineBody, MoneyOverview, MoneyOverviewQuery, SyncStarted, UsageVsBillingPage, UsageVsBillingQuery, ZohoInvoiceLine, ZohoInvoiceLinePage, } from './money.js';
import { API_BASE, defineRoute } from './route.js';
const MONEY = ['money.read'];
const READ = { auth: 'session', anyOf: MONEY, deny: 'forbidden', note: 'Managers (founder, associate director). Others: 403.' };
const WRITE = {
    auth: 'session',
    anyOf: MONEY,
    deny: 'forbidden',
    note: 'Managers (founder, associate director). Others: 403. Not while viewing as someone (403). Audited.',
};
export const step10Routes = {
    // ============================================================ Money → Revenue vs usage
    'money.overview': defineRoute({
        id: 'money.overview',
        method: 'GET',
        path: `${API_BASE}/money/overview`,
        summary: 'Money → Revenue vs usage: every client’s MRR, weighted use, zone and effort flag, with the four KPI tiles',
        description: 'Zones from the latest client_zones snapshot (§8.10). Revenue at risk here is Σ mrr of revenue_at_risk-zone clients — ' +
            'a different number from Today’s tile (annual revenue of at-risk-band clients); `labels` says which. Before the first ' +
            'successful Zoho sync the per-product MRR typed in Settings is the source (freshness.source = manual).',
        access: READ,
        query: MoneyOverviewQuery,
        success: { status: 200, description: 'The overview', schema: MoneyOverview },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'money.usageVsBilling': defineRoute({
        id: 'money.usageVsBilling',
        method: 'GET',
        path: `${API_BASE}/money/usage-vs-billing`,
        summary: 'Money → Usage by client: per client per product, what they bought and pay against what they used, with the gap',
        description: 'Paged by client, largest unused value first. An unknown client_id is an empty page. Invoice lines with no product ' +
            'are counted in totals.unmapped_billed_lakh, never spread over products.',
        access: READ,
        query: UsageVsBillingQuery,
        success: { status: 200, description: 'Billing against usage', schema: UsageVsBillingPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ Money → Risk flags (the 12 rules, live)
    'money.flags': defineRoute({
        id: 'money.flags',
        method: 'GET',
        path: `${API_BASE}/money/flags`,
        summary: 'Money → Risk flags: hits of the 12 rules, worst first, with the subject, its numbers and the stat tiles',
        description: 'Default state=open. A flag switched off in Settings has no open hits (switching off clears them).',
        access: READ,
        query: ListFlagHitsQuery,
        success: { status: 200, description: 'Flag hits', schema: FlagHitPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'money.flagEscalate': defineRoute({
        id: 'money.flagEscalate',
        method: 'POST',
        path: `${API_BASE}/money/flags/{id}/escalate`,
        summary: 'Escalate: raise an escalation from this flag; the hit becomes acknowledged (how: escalated)',
        description: '409 when the hit is not open, or when it is not about a client (an escalation needs one). The escalation’s owner is ' +
            'the client’s current SPOC unless owner_id is given (needs task.reassign). The default title is "<rule>: <text>" ' +
            'without the revenue figure the manager sees on the flag (the owner is usually a member, who must never see it); ' +
            'a title you type is kept as typed.',
        access: WRITE,
        params: FlagHitIdParams,
        headers: IdempotencyHeaders,
        body: EscalateFlagBody,
        success: { status: 201, description: 'The escalation', schema: Escalation },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'money.flagTask': defineRoute({
        id: 'money.flagTask',
        method: 'POST',
        path: `${API_BASE}/money/flags/{id}/task`,
        summary: 'Make task: create a task from this flag; the hit becomes acknowledged (how: task_made)',
        description: '409 when the hit is not open. Defaults: assignee the client’s SPOC (picking someone else needs task.reassign), ' +
            'due the next working day, priority high for a high hit else medium, title "<rule>: <text>" ' +
            'without the revenue figure the manager sees on the flag (the assignee is usually a member, who must never see it); ' +
            'a title you type is kept as typed.',
        access: WRITE,
        params: FlagHitIdParams,
        headers: IdempotencyHeaders,
        body: FlagTaskBody,
        success: { status: 201, description: 'The task', schema: Task },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'money.flagAck': defineRoute({
        id: 'money.flagAck',
        method: 'POST',
        path: `${API_BASE}/money/flags/{id}/ack`,
        summary: 'Acknowledge a flag once you have acted on it',
        description: 'Idempotent on an acknowledged hit (returns it unchanged); 409 on a cleared one.',
        access: WRITE,
        params: FlagHitIdParams,
        body: AckFlagBody,
        success: { status: 200, description: 'The hit, acknowledged', schema: FlagHit },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ Settings → Integrations, Zoho
    'integrations.list': defineRoute({
        id: 'integrations.list',
        method: 'GET',
        path: `${API_BASE}/integrations`,
        summary: 'Settings → Integrations: each integration’s state, last success and last error',
        description: 'Always all four, in INTEGRATION_KEYS order; a never-configured one is status off.',
        access: READ,
        success: { status: 200, description: 'Integrations', schema: IntegrationList },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'integrations.zohoSync': defineRoute({
        id: 'integrations.zohoSync',
        method: 'POST',
        path: `${API_BASE}/integrations/zoho/sync`,
        summary: 'Sync now: a manual run of the nightly Zoho pull (clients, plans, invoices)',
        description: 'Queues zoho.sync and returns its job_runs id at once. 409 while a Zoho run is already running, or when Zoho is not ' +
            'set up (integration off). A failed run changes nothing: the previous figures stand and the staleness banner shows.',
        access: WRITE,
        success: { status: 202, description: 'Queued', schema: SyncStarted },
        errors: ['no_session', 'forbidden', 'conflict'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'integrations.zohoInvoiceLines': defineRoute({
        id: 'integrations.zohoInvoiceLines',
        method: 'GET',
        path: `${API_BASE}/integrations/zoho/invoice-lines`,
        summary: 'Invoice lines needing a product (?mapped=false), or all lines',
        access: READ,
        query: ListInvoiceLinesQuery,
        success: { status: 200, description: 'Invoice lines, newest invoice first', schema: ZohoInvoiceLinePage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'integrations.zohoMapLine': defineRoute({
        id: 'integrations.zohoMapLine',
        method: 'POST',
        path: `${API_BASE}/integrations/zoho/invoice-lines/{id}/map`,
        summary: 'Say which product an invoice line is (unmapped lines are shown, never guessed)',
        description: '422 when product_key is not a product this client buys; 409 when the line has no matched client. Re-mapping is ' +
            'allowed and audited. Mapping the same product again returns the line unchanged.',
        access: WRITE,
        params: InvoiceLineIdParams,
        body: MapInvoiceLineBody,
        success: { status: 200, description: 'The mapped line', schema: ZohoInvoiceLine },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ operations view
    'jobs.runs': defineRoute({
        id: 'jobs.runs',
        method: 'GET',
        path: `${API_BASE}/jobs/runs`,
        summary: 'Background job runs, newest first — the operations view',
        description: 'Default: runs started in the last 7 days.',
        access: READ,
        query: ListJobRunsQuery,
        success: { status: 200, description: 'Job runs', schema: JobRunPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.step10.js.map